/**
 * AI Detection Service - Komby AI Hub
 * Detects and validates OpenAI configuration
 */
import { logger } from './logger';

interface AIConfigStatus {
  isConfigured: boolean;
  hasValidKey: boolean;
  keyFormat: 'valid' | 'placeholder' | 'missing' | 'invalid';
  model: string;
  confidenceThreshold: number;
  lastChecked: Date;
}

/**
 * Détecte si l'API OpenAI est correctement configurée
 */
export const detectAIConfiguration = (): AIConfigStatus => {
  const openaiKey = import.meta.env.VITE_OPENAI_API_KEY;
  const model = import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o-mini';
  const threshold = parseFloat(import.meta.env.VITE_AI_CONFIDENCE_THRESHOLD || '0.7');

  logger.debug('AI Detection - Checking configuration...');

  // Vérification format clé
  let keyFormat: AIConfigStatus['keyFormat'] = 'missing';
  let hasValidKey = false;

  if (!openaiKey) {
    keyFormat = 'missing';
  } else if (openaiKey === 'COLLER_VOTRE_CLE_ICI') {
    keyFormat = 'placeholder';
  } else if (openaiKey.startsWith('sk-proj-') || openaiKey.startsWith('sk-')) {
    keyFormat = 'valid';
    hasValidKey = true;
  } else {
    keyFormat = 'invalid';
  }

  const isConfigured = hasValidKey && !!model && threshold > 0;

  logger.debug(`AI Status: ${isConfigured ? 'Configured' : 'Not configured'}, Key Format: ${keyFormat}`);

  return {
    isConfigured,
    hasValidKey,
    keyFormat,
    model,
    confidenceThreshold: threshold,
    lastChecked: new Date()
  };
};

/**
 * Test de connexion à l'API OpenAI
 * 
 * ⚠️ SÉCURITÉ CRITIQUE :
 * - Les appels directs à OpenAI depuis le navigateur sont BLOQUÉS par CORS
 * - Exposer VITE_OPENAI_API_KEY dans le front-end est DANGEREUX (clé visible publiquement)
 * - Ce test vérifie uniquement le FORMAT de la clé, SANS appel réseau
 * 
 * 🔒 SOLUTION SÉCURISÉE :
 * - Déplacer les appels OpenAI vers un backend Node.js sécurisé
 * - Utiliser OPENAI_API_KEY (sans VITE_) côté serveur uniquement
 * - Le front-end appelle votre backend qui fait le proxy vers OpenAI
 */
export const testAIConnection = async (): Promise<{
  success: boolean;
  error?: string;
  responseTime?: number;
}> => {
  const config = detectAIConfiguration();
  
  if (!config.hasValidKey) {
    return {
      success: false,
      error: 'Clé API OpenAI manquante ou invalide'
    };
  }

  // Format validation only (no network call)
  logger.debug('OpenAI API key format validated (sk-proj-...). Use backend proxy for secure integration.');
  
  return {
    success: true,
    responseTime: 0,
  };
  
  // ❌ CODE DÉSACTIVÉ POUR SÉCURITÉ
  // Ne JAMAIS appeler OpenAI directement depuis le navigateur :
  // 1. CORS bloque les requêtes
  // 2. Clé API exposée publiquement dans le code JavaScript
  // 3. N'importe qui peut voler votre clé en ouvrant DevTools
  /*
  const startTime = Date.now();

  try {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    const response = await fetch('https://api.openai.com/v1/models', {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      }
    });

    const responseTime = Date.now() - startTime;

    if (response.ok) {
      logger.debug('OpenAI API connection successful');
      return {
        success: true,
        responseTime
      };
    } else {
      const error = await response.text();
      console.error('❌ OpenAI API error:', error);
      return {
        success: false,
        error: `API Error: ${response.status}`
      };
    }
  } catch (error) {
    console.error('❌ OpenAI connection failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
  */
};

/**
 * Status de configuration pour l'UI
 */
export const getAIStatusMessage = (status: AIConfigStatus): {
  message: string;
  type: 'success' | 'warning' | 'error';
  icon: string;
} => {
  if (status.isConfigured) {
    return {
      message: '🤖 IA activée - Hub opérationnel',
      type: 'success',
      icon: '✅'
    };
  }

  switch (status.keyFormat) {
    case 'missing':
      return {
        message: '🔑 Clé OpenAI manquante - IA désactivée',
        type: 'error',
        icon: '❌'
      };
    case 'placeholder':
      return {
        message: '📝 Remplacez "COLLER_VOTRE_CLE_ICI" par votre vraie clé OpenAI',
        type: 'warning',
        icon: '⚠️'
      };
    case 'invalid':
      return {
        message: '🔑 Format de clé OpenAI invalide',
        type: 'error',
        icon: '❌'
      };
    default:
      return {
        message: '🔧 Configuration IA incomplète',
        type: 'warning',
        icon: '⚙️'
      };
  }
}; 